import { c as _c } from "react/compiler-runtime";
import { useQuery, type QueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@clerk/nextjs";
import { useUserSettings } from "@sukha/query";
import { getActivePersonality, getFocusMessages, type MemberFocusMessagesResponse } from "@/hooks/assistant/assistantApi";
import { speakLocaleForMemberCue } from "@/hooks/focusMessages/speakLocale";
import { clearAssistantAudioCache } from "@/hooks/useAssistantAudio";
import { normalizeUiLocale, type MemberUiLocale } from "@/i18n/locale";
export type { MemberFocusMessagesResponse };

/** Member-facing focus message cache key (scoped by assistant personality + UI locale). */
export function focusMessagesQueryKey(type: string, personalityId?: string | null, locale?: MemberUiLocale | null): readonly ["focusMessages", string, string, string] {
  return ["focusMessages", personalityId ?? "unknown", type, locale ?? "default"] as const;
}

/** Refetch spoken lines and drop stale preloaded audio (locale, personality, admin edits). */
export function refreshMemberFocusMessages(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    queryKey: ["focusMessages"]
  });
  clearAssistantAudioCache();
}

/** Current member UI locale from settings (optimistic-aware). */
export function useMemberUiLocale() {
  const $ = _c(2);
  const {
    data: userSettings
  } = useUserSettings();
  const t0 = userSettings?.locale;
  let t1;
  if ($[0] !== t0) {
    t1 = normalizeUiLocale(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Active assistant personality id for the signed-in member (from settings or default).
 */
export function useActiveAssistantPersonalityId() {
  const $ = _c(3);
  const {
    getToken
  } = useAuth();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["user-selected-personality"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== getToken) {
    t1 = {
      queryKey: t0,
      queryFn: async () => {
        const token = (await getToken().catch(_temp)) ?? undefined;
        return getActivePersonality(token);
      },
      staleTime: 600000,
      gcTime: 600000
    };
    $[1] = getToken;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data
  } = useQuery(t1);
  return data?.assistantPersonality?.assistantPersonality ?? undefined;
}

/**
 * Personality-aware focus messages for a single type (session start, nudges, etc.).
 */
function _temp() {}
export function useFocusMessages(type) {
  const $ = _c(11);
  const {
    getToken
  } = useAuth();
  const personalityId = useActiveAssistantPersonalityId();
  const memberLocale = useMemberUiLocale();
  let t0;
  if ($[0] !== memberLocale || $[1] !== personalityId || $[2] !== type) {
    t0 = focusMessagesQueryKey(type, personalityId, memberLocale);
    $[0] = memberLocale;
    $[1] = personalityId;
    $[2] = type;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== getToken || $[5] !== memberLocale || $[6] !== type) {
    t1 = async () => {
      const token = (await getToken().catch(_temp2)) ?? undefined;
      return getFocusMessages(type, token, memberLocale);
    };
    $[4] = getToken;
    $[5] = memberLocale;
    $[6] = type;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t0 || $[9] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: 120000,
      gcTime: 120000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return useQuery(t2);
}

/** TTS locale for a focus message pool (member locale, with English fallback lines). */
function _temp2() {}
export function useFocusMessageSpeakLocale(type) {
  const $ = _c(3);
  const memberLocale = useMemberUiLocale();
  const {
    data
  } = useFocusMessages(type);
  const t0 = data?.resolvedLocale;
  let t1;
  if ($[0] !== memberLocale || $[1] !== t0) {
    t1 = speakLocaleForMemberCue(memberLocale, t0);
    $[0] = memberLocale;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/** Convenience helper for hooks that only need message lines. */
export function useFocusMessageLines(type) {
  const $ = _c(2);
  const {
    data
  } = useFocusMessages(type);
  let t0;
  if ($[0] !== data?.messages) {
    t0 = data?.messages ?? [];
    $[0] = data?.messages;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}